import React, { useEffect, useRef, useState } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

interface VideoThumbnailProps {
  videoSrc: string;
  thumbnailTime: number; // Time in seconds for the thumbnail frame
}

const VideoThumbnail: React.FC<VideoThumbnailProps> = ({ videoSrc, thumbnailTime }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [poster, setPoster] = useState<string>("");
  const [videoLoaded, setVideoLoaded] = useState(false);

  useEffect(() => {
    // Ensure videoRef.current is in the DOM
    if (!videoRef.current) return;

    const checkDomAndInitPlayer = () => {
      if (document.body.contains(videoRef.current!)) {
        const player = videojs(videoRef.current!);

        const generateThumbnail = () => {
          player.load();
          player.one("loadedmetadata", () => {
            player.currentTime(thumbnailTime);

            player.one("seeked", () => {
              const canvas = document.createElement("canvas");
              canvas.width = player.videoWidth();
              canvas.height = player.videoHeight();

              const ctx = canvas.getContext("2d");
              if (ctx && videoRef.current) {
                ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
                const dataURL = canvas.toDataURL();
                setPoster(dataURL);
              }

              player.currentTime(0); // Reset the video to the start
            });
          });
        };

        player.ready(() => {
          generateThumbnail();
        });

        return () => {
          player.dispose(); // Clean up Video.js instance
        };
      } else {
        console.warn("Video element is not yet in the DOM, retrying...");
        requestAnimationFrame(checkDomAndInitPlayer);
      }
    };

    checkDomAndInitPlayer();
  }, [videoSrc, thumbnailTime]);

  const handleLoadedMetadata = () => {
    setVideoLoaded(true);
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        justifyContent: "center",
      }}
    >
      <video
        crossOrigin="anonymous"
        ref={videoRef}
        className="video-js"
        controls
        preload="metadata"
        poster={poster}
        playsInline
        onLoadedMetadata={handleLoadedMetadata}
        style={{
          width: "100%",
          height: videoLoaded ? "auto" : "400px",
          objectFit: "contain",
          backgroundColor: "transparent",
        }}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
    </div>
  );
};

export default VideoThumbnail;
